import React from 'react';
import { ButtonLink, InlineLink, ResourceLink } from '../shared/AcademicLink';

const MANTStudies = () => {
  const coreCourses = [
    { code: 'BIB 506', name: 'Advanced Study of the NT', credits: 3 },
    { code: 'BIB 608', name: 'Study of the Gospels', credits: 3 },
    { code: 'BIB 609', name: 'Study of Acts', credits: 3 },
    { code: 'BIB 610', name: 'Study of Church Epistles', credits: 3 },
    { code: 'BIB 611', name: 'Study of Pastoral Epistles', credits: 3 },
    { code: 'BIB 612', name: 'Study of General Epistles', credits: 3 },
    { code: 'BIB 613', name: 'Study of Revelation', credits: 3 }
  ];

  const electiveCourses = [
    { code: 'BIB 531', name: 'Elementary Greek 1', credits: 3 },
    { code: 'BIB 532', name: 'Elementary Greek 2', credits: 3 },
    { code: 'BIB 533', name: 'Advanced Greek 1', credits: 3 },
    { code: 'BIB 534', name: 'Advanced Greek 2', credits: 3 },
    { code: 'BIB 622', name: 'New Testament Textual Criticism', credits: 3 },
    { code: 'BIB 522', name: 'Introduction to Biblical Ethics', credits: 3 }
  ];

  const programFeatures = [
    {
      title: "Biblical Greek",
      description: "Master the original language of the New Testament",
      icon: "🔤"
    },
    {
      title: "Early Christianity",
      description: "Study the historical context of the early church",
      icon: "⛪"
    },
    {
      title: "Textual Analysis",
      description: "Learn advanced methods of biblical interpretation",
      icon: "📜"
    },
    {
      title: "Contemporary Application",
      description: "Connect ancient texts with modern ministry",
      icon: "🔄"
    }
  ];

  const resources = [
    {
      title: "Greek Language Lab",
      description: "Access to advanced Greek language resources and tools",
      path: "/academics/resources/greek_lab"
    },
    {
      title: "Early Church Studies",
      description: "Resources for understanding early Christian history",
      path: "/academics/resources/early_church"
    },
    {
      title: "Research Materials",
      description: "Access to scholarly journals and publications",
      path: "/academics/resources/research"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              Master of Arts in New Testament Studies
            </h1>
            <p className="text-xl opacity-90">
              Advanced scholarship in New Testament interpretation and early Christian studies
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Program Overview</h2>
              <p className="text-gray-600 mb-4">
                The MA in New Testament Studies provides comprehensive training in biblical Greek, 
                early Christian history, and New Testament interpretation. Through this program, 
                you'll gain the skills to engage with New Testament texts in their original language 
                and historical context.
              </p>
            </div>
          </section>

          {/* Program Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {programFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{feature.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Core Courses */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Core Courses (21 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {coreCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Elective Courses */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Elective Courses (Select 3 courses - 9 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {electiveCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Resources Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Resources</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {resources.map((resource, index) => (
                  <ResourceLink
                    key={index}
                    to={resource.path}
                    title={resource.title}
                    description={resource.description}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Begin Your Academic Journey</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Join our community of scholars dedicated to New Testament research and interpretation.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <ButtonLink to="/application" primary>
                  Apply Now
                </ButtonLink>
                <ButtonLink to="/request" primary={false}>
                  Request Information
                </ButtonLink>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MANTStudies; 